import { useEffect } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  label?: string;
}

export function ProgressIndicator({ currentStep, totalSteps, label }: ProgressIndicatorProps) {
  const progress = (currentStep / totalSteps) * 100;
  const animatedWidth = useSharedValue(0);
  const glowPulse = useSharedValue(0);

  useEffect(() => {
    animatedWidth.value = withSpring(progress, { damping: 20, stiffness: 80 });
    glowPulse.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1000, easing: Easing.inOut(Easing.sin) }),
        withTiming(0, { duration: 1000, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );
  }, [progress]);

  const fillStyle = useAnimatedStyle(() => ({
    width: `${animatedWidth.value}%` as any,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 0 ${6 + glowPulse.value * 10}px rgba(59,130,246,${0.4 + glowPulse.value * 0.4})`,
        } as any)
      : {}),
  }));

  return (
    <View style={styles.container}>
      <View style={styles.barBackground}>
        <Animated.View style={[styles.barFill, fillStyle]} />
      </View>
      {label && (
        <Text style={styles.label}>
          {label} ({currentStep}/{totalSteps})
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  barBackground: {
    height: 4,
    backgroundColor: theme.colors.surface,
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
    borderRadius: 2,
  },
  label: {
    marginTop: theme.spacing.xs,
    fontSize: 12,
    color: theme.colors.textLight,
    fontFamily: theme.fonts.regular,
    textAlign: 'center',
  },
});
